import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Http } from './http';
import { AppResponse } from '../types/http-response';

export type BatchStatus = 'draft' |'pending' | 'processing' | 'completed' | 'partial' | 'failed';

export interface BatchUploadedByUser {
  id: string;
  name: string;
  email: string;
}

export interface UploadBatch {
  id: string;
  batchRef: string;
  fileName: string;
  uploadedAt: string;
  totalRecords: number;
  processedRecords: number;
  failedRecords: number;
  status: BatchStatus;
  uploadedByUser: BatchUploadedByUser | null;
  productInfo?: {
    productName: string;
    brandName: string;
    batchNo: string;
    uid: string;
  } | null;
  selected?: boolean;   // UI-only flag
}

export interface BatchListResult {
  rows: UploadBatch[];
  total: number;
  page: number;
  limit: number;
}

export interface BatchListParams {
  search?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  page?: number;
  limit?: number;
  fromDate?: string;
  toDate?: string;
  status?: string;
  uploadedBy?: string;
}

export type ProductStatus = 'pending' | 'image_mapped' | 'submitted' | 'qr_generated' | 'failed';

export interface BatchProduct {
  id: string;
  uid: string;
  gtin: string;
  genericName: string;
  brandName: string;
  batchNo: string;
  expiryDate: string;
  status: ProductStatus;
  qrCodePath: string | null;
  primaryImageId: string | null;
  selected?: boolean; // UI-only
}

export interface BatchProductsResult {
  rows: BatchProduct[];
  total: number;
  page: number;
  limit: number;
}

export interface BatchProductsParams {
  search?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface CreateBatchResult {
  batchId: string;
  batchRef: string;
  totalRecords: number;
  status: 'pending';
}

@Injectable({ providedIn: 'root' })
export class BatchService {
  constructor(private http: Http) {}

  getBatches(params: BatchListParams = {}): Observable<AppResponse<BatchListResult>> {
    const qs = new URLSearchParams();
    if (params.search)      qs.set('search',     params.search);
    if (params.sortBy)       qs.set('sortBy',     params.sortBy);
    if (params.sortDir)      qs.set('sortDir',    params.sortDir);
    if (params.page)         qs.set('page',       String(params.page));
    if (params.limit)        qs.set('limit',      String(params.limit));
    if (params.fromDate)     qs.set('fromDate',   params.fromDate);
    if (params.toDate)       qs.set('toDate',     params.toDate);
    if (params.status)       qs.set('status',     params.status);
    if (params.uploadedBy)   qs.set('uploadedBy', params.uploadedBy);
    const query = qs.toString() ? `?${qs.toString()}` : '';
    return this.http.get<BatchListResult>(`/batches${query}`);
  }

  getBatchById(id: string): Observable<AppResponse<UploadBatch>> {
    return this.http.get<UploadBatch>(`/batches/${id}`);
  }

  getBatchProducts(id: string, params: BatchProductsParams = {}): Observable<AppResponse<BatchProductsResult>> {
    const qs = new URLSearchParams();
    if (params.search)  qs.set('search',  params.search);
    if (params.sortBy)  qs.set('sortBy',  params.sortBy);
    if (params.sortDir) qs.set('sortDir', params.sortDir);
    if (params.page)    qs.set('page',    String(params.page));
    if (params.limit)   qs.set('limit',   String(params.limit));
    const query = qs.toString() ? `?${qs.toString()}` : '';
    return this.http.get<BatchProductsResult>(`/batches/${id}/products${query}`);
  }

  downloadBatchQrZip(id: string): Observable<Blob> {
    return this.http.download(`/batches/${id}/qrcodes/download`);
  }

  /** Download QR ZIP for multiple batches via ?batchIds=id1,id2,... */
  downloadBatchQrZipMulti(ids: string[]): Observable<Blob> {
    const first = ids[0];
    const query = ids.length > 1 ? `?batchIds=${ids.join(',')}` : '';
    return this.http.download(`/batches/${first}/qrcodes/download${query}`);
  }

  /**
   * POST /api/v1/batches/:id/qrcodes/download
   * Queues an async ZIP build; returns { requestId, status: 'pending' }.
   * The server will emit a `zip_ready` WebSocket event when the ZIP is done.
   */
  requestBatchDownload(batchId: string): Observable<AppResponse<{ requestId: string; status: string }>> {
    return this.http.postSilent<{ requestId: string; status: string }>(
      `/batches/${batchId}/qrcodes/download`,
      {},
    );
  }

  /**
   * GET /api/v1/batches/downloads/:requestId
   * Streams the pre-built ZIP for a completed download request.
   */
  fetchDownloadFile(requestId: string): Observable<Blob> {
    return this.http.download(`/batches/downloads/${requestId}`);
  }

  /** PATCH /api/v1/batches/:id/status — transition batch (and its products) from draft → pending */
  updateBatchStatus(id: string, status: 'pending'): Observable<AppResponse<null>> {
    return this.http.patch<null>(`/batches/${id}/status`, { status });
  }

  /** POST /api/v1/batches — create a batch from form data (multipart/form-data). */
  createBatch(formData: FormData): Observable<AppResponse<CreateBatchResult>> {
    return this.http.upload<CreateBatchResult>('/batches', formData);
  }
}
